import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { XIcon } from './Icons';

const Modal = ({ 
  isOpen, 
  onClose, 
  children, 
  className,
  size = "default"
}) => {
  const { theme } = useTheme();
  
  if (!isOpen) return null;
  
  const sizeClasses = {
    sm: "max-w-md",
    default: "max-w-2xl",
    lg: "max-w-4xl",
    xl: "max-w-6xl",
    full: "max-w-[90vw]"
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" 
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className={cn(
        `relative w-full mx-4 ${theme.colors.surface} rounded-xl ${theme.shadow.xl}`,
        sizeClasses[size],
        className
      )}>
        {children}
      </div>
    </div>
  );
};

const ModalHeader = ({ className, children, onClose, ...props }) => {
  const { theme } = useTheme();
  
  return (
    <div
      className={cn(
        `px-6 py-4 ${theme.border.light} border-b`,
        className
      )}
      {...props}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">{children}</div>
        {onClose && (
          <button
            onClick={onClose}
            className={`p-2 hover:${theme.colors.background} rounded-lg transition-colors ml-4`}
          >
            <XIcon className={`w-5 h-5 ${theme.text.muted}`} />
          </button>
        )}
      </div>
    </div>
  );
};

const ModalTitle = ({ className, ...props }) => {
  const { theme } = useTheme();
  
  return (
    <h3
      className={cn(
        `text-xl font-semibold ${theme.text.primary}`,
        className
      )}
      {...props}
    />
  );
};

const ModalDescription = ({ className, ...props }) => {
  const { theme } = useTheme();
  
  return (
    <p
      className={cn(
        `text-sm ${theme.text.muted} mt-1`,
        className
      )}
      {...props}
    />
  );
};

const ModalContent = ({ className, maxHeight, ...props }) => {
  const heightClass = maxHeight ? `max-h-${maxHeight} overflow-y-auto` : '';
  
  return (
    <div
      className={cn(
        "p-6",
        heightClass,
        className
      )}
      {...props}
    />
  );
};

const ModalFooter = ({ className, ...props }) => {
  const { theme } = useTheme();
  
  return (
    <div
      className={cn(
        `px-6 py-4 ${theme.border.light} border-t`,
        className
      )}
      {...props}
    />
  );
};

// Drawer component for slide-out panels
const Drawer = ({ 
  isOpen, 
  onClose, 
  children, 
  className,
  position = "right",
  width
}) => {
  const { theme } = useTheme();
  
  if (!isOpen) return null;
  
  const positionClasses = {
    right: "inset-y-0 right-0",
    left: "inset-y-0 left-0",
    top: "inset-x-0 top-0",
    bottom: "inset-x-0 bottom-0"
  };
  
  const sizeClasses = {
    right: width ? "" : "max-w-md w-full",
    left: width ? "" : "max-w-md w-full", 
    top: "max-h-96 h-full",
    bottom: "max-h-96 h-full"
  };
  
  // Determine if width is viewport-based (vw or %)
  const isViewportWidth = width && (width.includes('vw') || width.includes('%'));
  
  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" 
        onClick={onClose}
      />
      
      {/* Drawer Content */}
      <div 
        className={cn(
          `fixed ${positionClasses[position]} ${sizeClasses[position]} ${theme.colors.surface} ${theme.shadow['2xl']} transform transition-transform duration-300 ease-in-out flex flex-col`,
          className
        )}
        style={width ? { 
          width,
          maxWidth: isViewportWidth ? 'none' : '95vw' // Only set maxWidth for pixel values
        } : {}}
      >
        {children}
      </div>
    </div>
  );
};

const DrawerHeader = ModalHeader;
const DrawerTitle = ModalTitle;
const DrawerDescription = ModalDescription;
const DrawerContent = ({ className, ...props }) => (
  <div className={cn("flex-1 overflow-y-auto p-6", className)} {...props} />
);
const DrawerFooter = ({ className, ...props }) => {
  const { theme } = useTheme();
  
  return (
    <div
      className={cn(
        `px-6 py-4 ${theme.border.light} border-t flex-shrink-0`,
        className
      )}
      {...props}
    />
  );
};

export {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
  Drawer,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerContent,
  DrawerFooter,
};
